import { AxiosError } from 'axios';
import useSWRInfinite from 'swr/infinite';

import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

import { GROUP_COMMENTS } from '@/lib/routes/apiEndpoints';

const getKey = (args: {
    pageIndex: number;
    previousPageData: any;
    groupId: number | null;
    limit: number;
    requestParams: any;
}) => {
    const { pageIndex, previousPageData, groupId, limit, requestParams } = args;

    // No group id
    if (!groupId) return null;

    // Reached the end
    if (previousPageData && !previousPageData.next_page_url) return null;

    const url = injectEndpointUrlParams(GROUP_COMMENTS, {
        GROUP_ID: groupId
    });

    // Add the page parameter
    return [`${url}?per_page=${limit}&page=${pageIndex + 1}`, JSON.stringify(requestParams)];
};

type UseGroupConversationProps = {
    shouldFetch?: boolean;
    limit?: number;
    options?: Record<string, any>;
    params?: Record<string, any>;
};

export const useGroupConversation = ({
    shouldFetch = true,
    limit = 20,
    options = {},
    params = null
}: UseGroupConversationProps = {}) => {
    const { activeGroupId } = useProfilesStore((state) => state);

    const filterTopLevelComments = (comments: any[]) => {
        return comments.filter((comment) => !comment.parent_id);
    };

    const { data, size, setSize, error, isValidating, mutate } = useSWRInfinite<
        any,
        AxiosError<Error>
    >(
        shouldFetch
            ? (pageIndex, previousPageData) =>
                  getKey({
                      pageIndex,
                      previousPageData,
                      groupId: activeGroupId,
                      limit,
                      requestParams: params
                  })
            : null,
        ([url]) => get(url, params).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const isLoadingInitialData = !data && !error;
    const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating;
    const isLoadMore = data && data[data.length - 1]?.next_page_url !== null;

    // Flatten all comments from all pages and filter out comments with parent_id
    const allComments = data
        ? data.reduce((acc, page) => [...acc, ...filterTopLevelComments(page.comments)], [])
        : [];

    return {
        data: allComments,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        loadMore: () => setSize(size + 1),
        mutate
    };
};
